import type { EntityLocation, SearchMatch } from '@bower/shared';
import { apiService } from './api.service';
import { unwrapApiResponse } from './api/http-client.types';

export type EntityType =
  | 'project'
  | 'note'
  | 'attachment'
  | 'protocol'
  | 'equipment'
  | 'spreadsheet';
export type SortBy = 'relevance' | 'recent' | 'mixed';
/**
 * `keyword`: ranked name and body matches, no embedding call (the fast path).
 * `semantic`: meaning matches only. `hybrid`: both, keyword first.
 */
export type SearchMode = 'keyword' | 'hybrid' | 'semantic';

/** Where a row matched, from the server's ranking: `SEARCH_MATCH_TIER` in @bower/shared. */
export type { SearchMatch };

export interface SearchResult {
  type: EntityType;
  id: string;
  title: string;
  description?: string;
  snippet?: string;
  rank: number;
  updatedAt: string;
  createdById?: string;
  /** Source-of-origin literal from Artifact.captureMethod (Bower taxonomy:
   * audio_to_text, image_to_text, live_recording, agent_authored, …).
   * Absent on `project` rows. */
  captureMethod?: string;
  /** True for Bower-seeded onboarding content (starter protocols, welcome
   *  note, ORCID-imported publications). Keyed off captureSurface server-side,
   *  not the title — seeded publications carry real paper titles. */
  seeded?: boolean;
  /**
   * Ancestor collections, outermost first — the breadcrumb on a search row and
   * the second line of a move/copy destination row. Resolved server-side for
   * the returned page only; see `entity-location.service.ts`.
   *
   * Absent when the row's container is not a collection. Present with empty
   * `segments` and `truncated: false` when it sits at the workspace root.
   */
  location?: EntityLocation;
  match?: SearchMatch;
  /**
   * Set on a Live or protocol-run capture that stands for every capture of its
   * run (or Live session) this search found; the server folds the others into
   * it. `count` includes this row. `protocolId` and `label` ("Run 3 · PCR")
   * come only when the caller can read the protocol.
   */
  group?: {
    kind: 'run' | 'liveSession';
    id: string;
    count: number;
    protocolId?: string;
    label?: string;
  };
  metadata?: {
    projectId?: string;
    /** @deprecated Immediate parent name only. Read `location` instead. */
    projectName?: string;
    artifactType?: string;
    parentEntityType?: string;
    /** The collection this row sits directly inside. Absent at the workspace root. */
    parentCollectionId?: string;
  };
}

export interface SearchOptions {
  query: string;
  entityTypes?: EntityType[];
  limit?: number;
  offset?: number;
  sortBy?: SortBy;
  mode?: SearchMode;
  /** Empty query only: `mine` lists what the caller created or edited, newest first. */
  scope?: 'mine';
  /**
   * Fold a Live run's captures into one row per run (`group` on the row). Only
   * for lists a person browses; a picker, where one specific capture is the
   * answer, leaves it off.
   */
  groupGenerated?: boolean;
  /** Cancels the request when a newer query supersedes it (TanStack passes one). */
  signal?: AbortSignal;
}

export interface SearchPage {
  results: SearchResult[];
  total: number;
  offset: number;
  limit: number;
}

/**
 * Search service for global search functionality
 * Uses the centralized API service for backend communication
 */
class SearchService {
  /**
   * Perform global search across all entity types
   * Returns a flat array of results (no pagination metadata)
   */
  async search(options: SearchOptions): Promise<SearchResult[]> {
    const page = await this.searchPaginated(options);
    return page.results;
  }

  /**
   * Perform paginated global search — returns results + total count
   */
  async searchPaginated(options: SearchOptions): Promise<SearchPage> {
    const params: Record<string, string> = {
      q: options.query,
    };

    if (options.entityTypes && options.entityTypes.length > 0) {
      params.entityTypes = options.entityTypes.join(',');
    }

    if (options.limit) {
      params.limit = options.limit.toString();
    }

    if (options.offset) {
      params.offset = options.offset.toString();
    }

    if (options.sortBy) {
      params.sortBy = options.sortBy;
    }

    if (options.mode) {
      params.mode = options.mode;
    }

    if (options.scope) {
      params.scope = options.scope;
    }

    if (options.groupGenerated) {
      params.group = 'generated';
    }

    const response = await apiService.get<SearchResult[]>('/search', {
      params,
      ...(options.signal ? { signal: options.signal } : {}),
    });
    // Throw on a failure envelope. The client RESOLVES a 429 or a 5xx as
    // `{ success: false }`, and reading its missing `data` as `[]` told the
    // palette a rate-limited search had matched nothing ("0 results").
    const data = unwrapApiResponse(response, 'Search failed') ?? [];
    const meta = response.meta as Record<string, unknown> | undefined;

    return {
      results: data,
      total: (meta?.total as number) ?? data.length,
      offset: (meta?.offset as number) ?? 0,
      limit: (meta?.limit as number) ?? (options.limit || 20),
    };
  }
}

export const searchService = new SearchService();
